import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";

const FLAG_LABEL = {
    "no-insurance": "No insurance",
    "few-documents": "Few documents",
    "missing-nominee": "Missing nominee",
};

export default function AdvisorDashboard() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/advisor/clients")
            .then(({ data }) => setClients(data))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="advisor-page">
            <PageHeader
                eyebrow="Advisor Console"
                title="Every client, every risk, on one screen."
                subtitle="Move from reactive to proactive — see the gaps before clients call."
                testid="advisor-header"
            />

            <div className="px-8 py-8">
                {loading ? <LoadingBlock /> : clients.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border p-10 text-sm text-muted-foreground text-center">
                        No clients on the platform yet. Invite your first client by sharing the Sign-up link.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {clients.map((c) => (
                            <article
                                key={c.id}
                                data-testid={`client-card-${c.id}`}
                                className="rounded-lg border border-border bg-card p-5 hover:border-primary transition-colors duration-200"
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-display text-lg font-bold">{c.name || "Unnamed"}</div>
                                        <div className="text-xs font-mono text-muted-foreground">{c.email}</div>
                                    </div>
                                    {c.risk_flags?.length ? (
                                        <span className="text-xs font-semibold text-destructive">{c.risk_flags.length} flag{c.risk_flags.length > 1 ? "s" : ""}</span>
                                    ) : (
                                        <span className="text-xs font-semibold text-primary">All clear</span>
                                    )}
                                </div>
                                <div className="mt-4 grid grid-cols-3 text-center text-sm">
                                    <Stat n={c.counts.documents} l="docs" />
                                    <Stat n={c.counts.insurance} l="policies" />
                                    <Stat n={c.counts.accounts} l="accounts" />
                                </div>
                                {c.risk_flags?.length ? (
                                    <div className="mt-4 flex flex-wrap gap-1.5">
                                        {c.risk_flags.map((f) => (
                                            <Badge key={f} variant="outline" className="text-destructive border-destructive/40">
                                                {FLAG_LABEL[f] || f}
                                            </Badge>
                                        ))}
                                    </div>
                                ) : null}
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ n, l }) {
    return (
        <div>
            <div className="font-display font-bold text-xl font-mono">{n}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{l}</div>
        </div>
    );
}
